import { useEffect, useRef, useState } from "react";
import { BarcodeDetector as ZXingDetector, prepareZXingModule } from "barcode-detector/ponyfill";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLine, Keyboard, AlertTriangle, RefreshCw, Image as ImageIcon, Camera, Flashlight, FlashlightOff, Loader2, SwitchCamera, Bug, Copy } from "lucide-react";
import { PRODUCT_FORMATS, isProductBarcode, pickProductCode } from "@/lib/barcode";

// WASM okuyucu yerel sunucudan (CDN'e bağımlı değil) — public/zxing/zxing_reader.wasm (paketle AYNI sürüm olmalı)
prepareZXingModule({ overrides: { locateFile: (path, prefix) => (path.endsWith(".wasm") ? "/zxing/zxing_reader.wasm" : prefix + path) } });

const IS_IOS = typeof navigator !== "undefined" && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
// Yalnızca 1D ürün barkodları — QR / DataMatrix bilinçli olarak KAPALI (ambalajdaki web sitesi QR'ı okunmaz)
const FORMATS = PRODUCT_FORMATS;
const SCAN_EVERY_MS = 140;
const SERVER_EVERY_MS = 900;
const HYBRID_AFTER_MS = IS_IOS ? 700 : 2500;   // iPhone'da sunucu okuyucusu hemen devreye girer (odak/wasm sorunlarına karşı)
const HYBRID_EVERY_MS = IS_IOS ? 1100 : 1400;
const MAX_W = 1280;
const PHOTO_MAX = 2600;

// iPhone: çoklu lensli modellerde yakın odak ~20 cm; "Back Dual Wide Camera" lensler arası geçiş yapabildiği için barkodda en iyi sonucu verir
const LENS_RANK = [/back dual wide/i, /^back camera$|arka kamera$/i, /back triple/i, /back dual(?! wide)/i, /back|arka|rear|environment/i];
const LENS_AVOID = /ultra|telephoto|tele|front|ön|desk/i;
const rankLens = (label = "") => { if (LENS_AVOID.test(label) && !/dual wide/i.test(label)) return 99; const i = LENS_RANK.findIndex((r) => r.test(label)); return i < 0 ? 50 : i; };

const permissionHint = () => IS_IOS
  ? "Kamera izni verilmedi. Safari'de adres çubuğundaki \"aA\" → Web Sitesi Ayarları → Kamera: İzin Ver seçip 'Tekrar Dene'ye basın. (Ayarlar → Safari → Kamera 'Sor' veya 'İzin Ver' olmalı.)"
  : "Kamera izni reddedildi. Tarayıcı adres çubuğundaki kilit simgesinden kameraya izin verip 'Tekrar Dene'ye basın.";

const withTimeout = (p, ms, label) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error(`${label} zaman aşımı`)), ms))]);
const errText = (e) => String(e?.response?.data?.detail || e?.message || e).slice(0, 90);
const httpInfo = (e) => (e?.response?.status ? ` (HTTP ${e.response.status})` : e?.code ? ` (${e.code})` : "");

// Cihaz içi okuyucu: yerel BarcodeDetector (Android Chrome) → yoksa zxing-wasm. iOS'ta yerel API atlanır (eksik/boş sonuç verebiliyor).
async function makeDetector() {
  if (!IS_IOS && typeof window !== "undefined" && "BarcodeDetector" in window) {
    try {
      const sup = await window.BarcodeDetector.getSupportedFormats();
      if (sup.includes("ean_13") && sup.includes("code_128")) return { det: new window.BarcodeDetector({ formats: FORMATS.filter((f) => sup.includes(f)) }), kind: "native" };
    } catch { /* yerel API yok/bozuk → wasm */ }
  }
  const det = new ZXingDetector({ formats: FORMATS });
  const c = document.createElement("canvas"); c.width = 4; c.height = 4;
  await withTimeout(det.detect(c), 9000, "okuyucu yükleme");
  return { det, kind: "wasm" };
}

const canvasBlob = (canvas, q = 0.72) => new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", q));

async function decodeOnServer(blob, name = "frame.jpg") {
  const fd = new FormData();
  fd.append("file", blob, name);
  const { data } = await api.post("/barcode/decode", fd, { timeout: 45000 });
  return pickProductCode(data?.codes || []);
}

// Fotoğrafı tuvale çizer (EXIF yönü tarayıcı tarafından uygulanır) ve en fazla PHOTO_MAX px'e küçültür; başarısızsa null
async function fileToCanvas(file, maxPx = PHOTO_MAX) {
  let bmp = null;
  try { bmp = await createImageBitmap(file); } catch { bmp = null; }
  const canvas = document.createElement("canvas");
  if (bmp) {
    const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
    canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    try { bmp.close && bmp.close(); } catch { /* noop */ }
    return canvas;
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await withTimeout(new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = () => rej(new Error("görsel açılamadı")); im.src = url; }), 8000, "görsel açma");
    const scale = Math.min(1, maxPx / Math.max(img.naturalWidth, img.naturalHeight));
    canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  } catch { return null; }
  finally { URL.revokeObjectURL(url); }
}

// kare ortalama parlaklığı (0-255) — siyah/boş kamera görüntüsünü tanımak için
const frameBrightness = (ctx, w, h) => {
  try {
    const d = ctx.getImageData(Math.floor(w * 0.3), Math.floor(h * 0.3), Math.max(1, Math.floor(w * 0.4)), Math.max(1, Math.floor(h * 0.4))).data;
    let s = 0; const n = d.length / 4; const step = Math.max(1, Math.floor(n / 4000));
    for (let i = 0; i < d.length; i += 4 * step) s += (d[i] + d[i + 1] + d[i + 2]) / 3;
    return s / (n / step);
  } catch { return -1; }
};

export default function BarcodeScanner({ open, onClose, onDetected, continuous = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);      // {det, kind} | null
  const modeRef = useRef("local");       // local | server
  const loopRef = useRef({ stop: true, timer: null });
  const pickingRef = useRef(false);      // fotoğraf seçici açıkken diyalog kapanmasın
  const camsRef = useRef([]);            // arka kamera listesi (label sıralı)
  const firedRef = useRef({ code: "", at: 0 });   // devamlı modda aynı barkodun peş peşe tekrarını engeller
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;
  const [manual, setManual] = useState("");
  const [status, setStatus] = useState("idle"); // idle | starting | running | error | decoding
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [camIdx, setCamIdx] = useState(0);
  const [camCount, setCamCount] = useState(0);
  const [camLabel, setCamLabel] = useState("");
  const [torch, setTorch] = useState({ avail: false, on: false });
  const [zoom, setZoom] = useState(null);
  const [diag, setDiag] = useState("");
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [hint, setHint] = useState("");

  const addLog = (msg) => setLog((l) => [...l.slice(-39), `${new Date().toLocaleTimeString("tr-TR")} ${msg}`]);

  const stopCamera = () => {
    loopRef.current.stop = true;
    if (loopRef.current.timer) { clearTimeout(loopRef.current.timer); loopRef.current.timer = null; }
    const s = streamRef.current;
    streamRef.current = null;
    if (s) s.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
    const v = videoRef.current;
    if (v) { try { v.pause(); } catch { /* noop */ } v.srcObject = null; }
  };

  const finish = (code, via) => {
    const val = String(code || "").trim();
    if (!val) return;
    if (via !== "elle" && !isProductBarcode(val)) { addLog(`✗ yok sayıldı (ürün barkodu değil, ${via}): ${val.slice(0, 40)}`); return; }
    if (continuous) {
      // Devamlı tarama: kamera açık kalır, arka arkaya okutulur. Aynı kod ~1,2 sn içinde yeniden görülürse
      // yok sayılır (kamera aynı barkodu sürekli okuyor); farklı kod anında geçer, kasıtlı tekrar için barkodu tekrar gösterin.
      const t = Date.now();
      if (via !== "elle" && val === firedRef.current.code && t - firedRef.current.at < 1200) return;
      firedRef.current = { code: val, at: t };
      addLog(`✓ okundu (${via}): ${val}`);
      try { navigator.vibrate && navigator.vibrate(50); } catch { /* noop */ }
      onDetectedRef.current(val);
      return;
    }
    addLog(`✓ okundu (${via}): ${val}`);
    stopCamera();
    try { navigator.vibrate && navigator.vibrate(60); } catch { /* noop */ }
    onDetectedRef.current(val);
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setTorch({ avail: false, on: false }); setZoom(null); setDiag(""); setHint("");
    if (attempt === 0 && camIdx === 0) setLog([`cihaz: ${IS_IOS ? "iOS" : "diğer"} · ${navigator.userAgent.slice(0, 70)}`]);

    const fail = (err) => {
      if (cancelled) return;
      setStatus("error");
      const name = err?.name || "";
      const msg = String(err?.message || err || "");
      addLog(`✗ kamera hatası: ${name} ${msg.slice(0, 80)}`);
      if (name === "NotAllowedError" || name === "SecurityError" || /permission|denied/i.test(msg)) setError(permissionHint());
      else if (name === "NotFoundError" || name === "DevicesNotFoundError") setError("Bu cihazda kamera bulunamadı. 'Fotoğraf Çek' ile okutun ya da barkodu elle girin.");
      else if (name === "NotReadableError" || name === "AbortError" || name === "TrackStartError") setError("Kamera başka bir uygulama/sekme tarafından kullanılıyor. Diğer kamera uygulamalarını kapatıp 'Tekrar Dene'ye basın.");
      else if (msg === "no-frames") setError("Kameradan görüntü alınamadı. 'Tekrar Dene'ye basın; olmazsa 'Fotoğraf Çek' ile okutun ya da barkodu elle girin.");
      else setError(`Kamera açılamadı (${name || msg.slice(0, 60) || "bilinmeyen"}). 'Fotoğraf Çek' ile barkodun fotoğrafını çekerek okutabilir ya da numarayı elle girebilirsiniz.`);
    };

    const gum = (c) => navigator.mediaDevices.getUserMedia(c);
    const openStream = async () => {
      // 1) izin + arka kamera listesi
      const cams = camsRef.current;
      if (cams.length && cams[camIdx]) {
        try { return await gum({ audio: false, video: { deviceId: { exact: cams[camIdx].deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } } }); }
        catch (e) { addLog(`lens açılamadı (${cams[camIdx].label}): ${e?.name}`); }
      }
      const tries = [
        { audio: false, video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } } },
        { audio: false, video: { facingMode: "environment" } },
        { audio: false, video: true },
      ];
      let last = null, stream = null;
      for (const c of tries) {
        if (cancelled) return null;
        try { stream = await gum(c); break; } catch (e) { last = e; if (e?.name === "NotAllowedError" || e?.name === "SecurityError") break; }
      }
      if (!stream) throw last || new Error("Kamera açılamadı");
      // 2) izin alındı → lensleri listele, iPhone'da en uygun arka lensi seç
      try {
        const devs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
        const backs = devs.filter((d) => rankLens(d.label) < 99 || !/front|ön/i.test(d.label)).sort((a, b) => rankLens(a.label) - rankLens(b.label));
        camsRef.current = backs;
        setCamCount(backs.length);
        addLog(`kameralar: ${devs.map((d) => d.label || d.deviceId.slice(0, 6)).join(" | ") || "etiket yok"}`);
        const curId = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
        const best = backs[camIdx] || backs[0];
        if (best && best.deviceId && best.deviceId !== curId && IS_IOS) {
          try {
            const s2 = await gum({ audio: false, video: { deviceId: { exact: best.deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } } });
            stream.getTracks().forEach((t) => t.stop());
            addLog(`lens değiştirildi → ${best.label}`);
            return s2;
          } catch (e) { addLog(`tercih edilen lens açılamadı: ${e?.name}`); }
        }
      } catch { /* enumerate desteklenmiyor */ }
      return stream;
    };

    const waitVideo = (v) => new Promise((resolve) => {
      if (v.readyState >= 2 && v.videoWidth) return resolve();
      const done = () => { v.removeEventListener("loadeddata", done); v.removeEventListener("loadedmetadata", done); resolve(); };
      v.addEventListener("loadeddata", done); v.addEventListener("loadedmetadata", done);
      setTimeout(resolve, 2500);
    });

    const scanLoop = async () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      let errors = 0, lastServer = 0, serverBusy = false, serverTries = 0, serverFails = 0, darkSince = 0, hinted = false;
      const startedAt = Date.now();
      const hybridTick = async (cv) => {
        if (serverBusy || Date.now() - startedAt < HYBRID_AFTER_MS || Date.now() - lastServer < HYBRID_EVERY_MS) return;
        serverBusy = true; lastServer = Date.now(); serverTries += 1;
        try {
          const blob = await canvasBlob(cv, 0.8);
          if (blob && !cancelled && !loopRef.current.stop) {
            const text = await decodeOnServer(blob);
            if (text && !cancelled && !loopRef.current.stop) { finish(text, "sunucu/kamera"); return; }
            if (serverTries === 1) addLog(`sunucu okuyucusu devrede (${cv.width}×${cv.height}, ${Math.round(blob.size / 1024)} KB)`);
          }
        } catch (e) { serverFails += 1; if (serverFails <= 2) addLog(`sunucu hatası: ${errText(e)}${httpInfo(e)}`); }
        finally { serverBusy = false; }
      };
      const step = async () => {
        if (cancelled || loopRef.current.stop) return;
        const v = videoRef.current;
        let wait = SCAN_EVERY_MS;
        try {
          if (v && v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) {
            const scale = Math.min(1, MAX_W / v.videoWidth);
            canvas.width = Math.round(v.videoWidth * scale);
            canvas.height = Math.round(v.videoHeight * scale);
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            // siyah görüntü tespiti (iOS'ta bazen akış açılır ama kare gelmez)
            const br = frameBrightness(ctx, canvas.width, canvas.height);
            if (br >= 0 && br < 6) { if (!darkSince) darkSince = Date.now(); else if (Date.now() - darkSince > 3000 && !hinted) { hinted = true; setHint("Kameradan siyah görüntü geliyor → 'Kamera Değiştir' veya 'Tekrar Dene'yi kullanın."); addLog("uyarı: 3 sn boyunca siyah kare"); } }
            else darkSince = 0;
            if (!hinted && Date.now() - startedAt > 7000) { hinted = true; setHint(IS_IOS ? "Okunmuyorsa: barkodu 20–25 cm uzakta, düz ve iyi ışıkta tutun; yakınlaştırmayı artırın ya da 'Fotoğraf Çek' kullanın." : "Okunmuyorsa barkodu çerçeveye daha yakın/düz tutun ya da 'Fotoğraf Çek' kullanın."); }
            if (modeRef.current === "local" && detectorRef.current) {
              const codes = await detectorRef.current.det.detect(canvas);
              errors = 0;
              const hit = pickProductCode(codes);
              if (hit) { finish(hit, detectorRef.current.kind); if (!continuous) return; }
              hybridTick(canvas);
            } else {
              wait = SERVER_EVERY_MS;
              if (Date.now() - lastServer >= SERVER_EVERY_MS) {
                lastServer = Date.now();
                const blob = await canvasBlob(canvas, 0.8);
                if (blob) {
                  try { const text = await decodeOnServer(blob); if (text) { finish(text, "sunucu"); if (!continuous) return; } }
                  catch (e) { serverFails += 1; if (serverFails <= 2) addLog(`sunucu hatası: ${errText(e)}${httpInfo(e)}`); }
                }
              }
            }
          } else if (Date.now() - startedAt > 8000) {
            fail(new Error("no-frames")); return;
          }
        } catch (e) {
          errors += 1;
          if (modeRef.current === "local" && errors >= 4) {
            modeRef.current = "server"; errors = 0;
            setDiag(`Cihaz içi okuyucu hata verdi (${String(e?.message || e).slice(0, 50)}) → sunucu okuyucusuna geçildi`);
            addLog(`cihaz içi okuyucu hatası → sunucu modu: ${errText(e)}`);
          } else if (errors >= 12) { fail(new Error(`Okuyucu hatası: ${String(e?.message || e).slice(0, 60)}`)); return; }
        }
        if (!cancelled && !loopRef.current.stop) loopRef.current.timer = setTimeout(step, wait);
      };
      loopRef.current.stop = false;
      step();
    };

    const start = async () => {
      setStatus("starting"); setError("");
      if (!window.isSecureContext) { setStatus("error"); setError("Kamera yalnızca güvenli (https) bağlantıda çalışır. Adresin https:// ile başladığından emin olun."); return; }
      if (!navigator.mediaDevices?.getUserMedia) { setStatus("error"); setError(IS_IOS ? "Bu tarayıcı kamerayı desteklemiyor. iPhone'da lütfen Safari kullanın ya da 'Fotoğraf Çek' ile okutun." : "Bu cihaz/tarayıcı kamerayı desteklemiyor. Elle giriş yapın."); return; }
      const detP = detectorRef.current ? Promise.resolve(detectorRef.current) : makeDetector().catch((e) => { setDiag(`Cihaz içi okuyucu yüklenemedi (${String(e?.message || e).slice(0, 50)}) → sunucu okuyucusu kullanılıyor`); addLog(`wasm yüklenemedi: ${errText(e)}`); return null; });
      try {
        const stream = await openStream();
        if (!stream || cancelled) { stream && stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        let v = videoRef.current;
        for (let i = 0; i < 20 && !v; i++) { await new Promise((r) => setTimeout(r, 50)); v = videoRef.current; } // eslint-disable-line no-await-in-loop
        if (!v) throw new Error("video yok");
        v.setAttribute("playsinline", ""); v.setAttribute("webkit-playsinline", ""); v.setAttribute("muted", "");
        v.muted = true; v.autoplay = true; v.playsInline = true;
        v.srcObject = stream;
        try { await v.play(); } catch { /* autoplay engeli → loadeddata sonrası tekrar */ }
        await waitVideo(v);
        if (v.paused) { try { await v.play(); } catch { /* noop */ } }
        const track = stream.getVideoTracks()[0];
        const label = track?.label || "";
        setCamLabel(label);
        try {
          const caps = track.getCapabilities ? track.getCapabilities() : {};
          if (caps.torch) setTorch({ avail: true, on: false });
          if (caps.focusMode && caps.focusMode.includes("continuous")) { try { await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }); } catch { /* noop */ } }
          if (caps.zoom && caps.zoom.max > caps.zoom.min) {
            const max = Math.min(caps.zoom.max, caps.zoom.min * 4 || caps.zoom.max);
            let cur = (track.getSettings && track.getSettings().zoom) || caps.zoom.min;
            // iPhone yakın odak sınırı (~20 cm) → varsayılan 1.5-2x yakınlaştırma barkodu büyütür
            if (IS_IOS) { const want = Math.min(max, Math.max(caps.zoom.min, 2)); try { await track.applyConstraints({ advanced: [{ zoom: want }] }); cur = want; } catch { /* noop */ } }
            setZoom({ min: caps.zoom.min, max, step: caps.zoom.step || 0.1, value: cur });
          }
        } catch { /* yetenek sorgusu desteklenmiyor */ }
        detectorRef.current = await detP;
        modeRef.current = detectorRef.current ? "local" : "server";
        const info = `${detectorRef.current ? (detectorRef.current.kind === "native" ? "cihaz (yerel)" : "cihaz (wasm)") : "sunucu"} · ${v.videoWidth}×${v.videoHeight}${label ? ` · ${label}` : ""}`;
        setDiag(`Okuyucu: ${info}`); addLog(`kamera açıldı · ${info}`);
        if (cancelled) return;
        setStatus("running");
        scanLoop();
      } catch (e) { fail(e); }
    };

    start();
    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line
  }, [open, attempt, camIdx]);

  const switchCamera = () => { const n = camsRef.current.length; if (n > 1) { stopCamera(); setCamIdx((i) => (i + 1) % n); } else setAttempt((a) => a + 1); };
  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torch.on }] }); setTorch((t) => ({ ...t, on: !t.on })); } catch { /* desteklenmiyor */ }
  };
  const applyZoom = async (val) => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    setZoom((z) => (z ? { ...z, value: val } : z));
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ zoom: val }] }); } catch { /* noop */ }
  };

  const submitManual = () => {
    if (!manual.trim()) return;
    finish(manual.trim(), "elle");
    setManual("");
  };

  // Fotoğraf / galeri: iPhone'da SUNUCU önce (kanıtlanmış yol), sonra cihaz içi; her adım tanı günlüğüne yazılır
  const onFile = async (e) => {
    const file = e.target.files?.[0]; pickingRef.current = false; if (!file) { addLog("fotoğraf seçilmedi"); return; } e.target.value = "";
    stopCamera();
    setStatus("decoding"); setError(""); setDiag("Fotoğraf okunuyor…"); setHint("");
    addLog(`fotoğraf: ${Math.round(file.size / 1024)} KB · ${file.type || "tür yok"} · ${file.name || ""}`);
    const canvas = await fileToCanvas(file);
    addLog(canvas ? `tuval: ${canvas.width}×${canvas.height}` : "tuval çizilemedi");
    const tryServer = async (blob, name, label) => {
      try {
        setDiag(`Sunucuda okunuyor (${label})…`);
        const t = await decodeOnServer(blob, name);
        addLog(`sunucu (${label}, ${Math.round(blob.size / 1024)} KB): ${t ? "OKUNDU" : "bulunamadı"}`);
        return t;
      } catch (err) { addLog(`sunucu (${label}) hata: ${errText(err)}${httpInfo(err)}`); throw err; }
    };
    const tryLocal = async () => {
      if (!canvas) return "";
      try {
        setDiag("Cihaz içi okuyucu deneniyor…");
        if (!detectorRef.current) detectorRef.current = await withTimeout(makeDetector(), 6000, "okuyucu");
        const codes = await withTimeout(detectorRef.current.det.detect(canvas), 8000, "okuma");
        const hit = pickProductCode(codes);
        addLog(`cihaz içi: ${hit ? "OKUNDU" : "bulunamadı"}`);
        return hit;
      } catch (err) { addLog(`cihaz içi hata: ${errText(err)}`); return ""; }
    };
    let netErr = null;
    try {
      const big = canvas ? await canvasBlob(canvas, 0.9) : null;
      const order = IS_IOS ? ["server", "local"] : ["local", "server"];
      for (const step of order) {
        let text = "";
        if (step === "local") text = await tryLocal();
        else {
          try {
            if (big) text = await tryServer(big, "photo.jpg", `${canvas.width}px`);
            if (!text && file.size <= 15 * 1024 * 1024) text = await tryServer(file, file.name || "photo.jpg", "orijinal");
          } catch (err) { netErr = err; }
        }
        if (text) { finish(text, step === "local" ? "cihaz/fotoğraf" : "sunucu/fotoğraf"); return; }
      }
      setStatus("error");
      if (netErr) setError(`Görsel sunucuda okunamadı (${errText(netErr)}${httpInfo(netErr)}). İnternet bağlantınızı kontrol edip tekrar deneyin ya da barkodu elle girin.`);
      else setError("Fotoğrafta barkod bulunamadı. Barkodu 15–25 cm mesafeden, düz, iyi ışıkta ve net (bulanık olmadan) çekip tekrar deneyin ya da numarayı elle girin.");
      setDiag(`Fotoğraf ${canvas ? `${canvas.width}×${canvas.height}` : "?"} · ${Math.round(file.size / 1024)} KB · ${file.type || "tür yok"}`);
    } catch (err) {
      setStatus("error"); addLog(`fotoğraf akışı hatası: ${errText(err)}`);
      setError(`Fotoğraf işlenemedi (${errText(err)}). Tekrar deneyin ya da barkodu elle girin.`);
    }
  };
  const copyLog = async () => { try { await navigator.clipboard.writeText(log.join("\n")); setDiag("Tanı günlüğü kopyalandı"); } catch { /* noop */ } };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { if (pickingRef.current) return; stopCamera(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner" onInteractOutside={(e) => { if (pickingRef.current) e.preventDefault(); }} onFocusOutside={(e) => { if (pickingRef.current) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-head text-2xl flex items-center gap-2"><ScanLine className="h-6 w-6 text-primary" />Barkod Tara</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden bg-black min-h-[240px]">
            <video ref={videoRef} playsInline muted autoPlay className="w-full h-auto block max-h-[52vh] object-cover" data-testid="scanner-video" />
            {status === "running" && (
              <>
                <div className="pointer-events-none absolute inset-x-[8%] top-1/2 -translate-y-1/2 h-[38%] rounded-lg border-2 border-primary/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
                <div className="pointer-events-none absolute inset-x-[10%] top-1/2 h-0.5 bg-primary/80 animate-pulse" />
                <div className="absolute right-2 top-2 flex flex-col gap-1.5">
                  {torch.avail && (
                    <button type="button" onClick={toggleTorch} className="h-9 w-9 rounded-full bg-black/55 text-white flex items-center justify-center backdrop-blur" title="Işık" data-testid="scanner-torch">
                      {torch.on ? <FlashlightOff className="h-4 w-4" /> : <Flashlight className="h-4 w-4" />}
                    </button>
                  )}
                  {(camCount > 1 || IS_IOS) && (
                    <button type="button" onClick={switchCamera} className="h-9 w-9 rounded-full bg-black/55 text-white flex items-center justify-center backdrop-blur" title="Kamera / lens değiştir" data-testid="scanner-switch-camera">
                      <SwitchCamera className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {hint && <div className="absolute inset-x-2 top-2 right-14 rounded-md bg-black/65 text-white text-[11px] leading-snug px-2.5 py-1.5 backdrop-blur" data-testid="scanner-hint">{hint}</div>}
                {zoom && (
                  <div className="absolute inset-x-3 bottom-2 flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 backdrop-blur">
                    <span className="text-[10px] font-bold text-white/80">Yakınlaştır</span>
                    <input type="range" min={zoom.min} max={zoom.max} step={zoom.step} value={zoom.value} onChange={(e) => applyZoom(Number(e.target.value))} className="flex-1 accent-[#F55913]" data-testid="scanner-zoom" />
                    <span className="text-[10px] font-bold text-white/80 tabular-nums">{Number(zoom.value).toFixed(1)}x</span>
                  </div>
                )}
              </>
            )}
            {(status === "starting" || status === "decoding") && (
              <div className="absolute inset-0 flex items-center justify-center gap-2 text-white/80 text-sm bg-black/40" data-testid="scanner-starting"><Loader2 className="h-4 w-4 animate-spin" />{status === "decoding" ? (diag || "Fotoğraf okunuyor…") : "Kamera başlatılıyor..."}</div>
            )}
            {status === "error" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6 bg-black/75" data-testid="scanner-error">
                <AlertTriangle className="h-8 w-8 text-primary" />
                <p className="text-white/85 text-sm leading-relaxed">{error}</p>
                <Button size="sm" variant="secondary" onClick={() => setAttempt((a) => a + 1)} data-testid="scanner-retry"><RefreshCw className="h-4 w-4 mr-1.5" />Tekrar Dene</Button>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground text-center">{IS_IOS ? "Ürün barkodunu (çizgili kod) turuncu çerçeveye alın, 20–25 cm uzakta ve düz tutun; gerekirse yakınlaştırın. Kamera okumazsa 'Fotoğraf Çek' ile net bir fotoğraf çekin." : "Ürün barkodunu (çizgili kod) turuncu çerçeveye hizalayın; otomatik okunur. Ambalajdaki QR kodlar okunmaz. Karanlıkta ışık düğmesini kullanın."}</p>
          {(diag || camLabel) && <p className="text-[10px] text-muted-foreground/80 text-center font-mono leading-snug" data-testid="scanner-diag">{diag}</p>}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center justify-center gap-2 cursor-pointer rounded-lg border border-primary/60 bg-primary/5 py-2.5 text-sm font-bold text-primary hover:bg-primary/10" data-testid="barcode-capture-label" onClick={() => { pickingRef.current = true; setTimeout(() => { pickingRef.current = false; }, 120000); }}>
              <Camera className="h-4 w-4" />Fotoğraf Çek
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="barcode-capture" />
            </label>
            <label className="flex items-center justify-center gap-2 cursor-pointer rounded-lg border border-dashed py-2.5 text-sm text-muted-foreground hover:border-primary hover:text-primary" data-testid="barcode-gallery-label" onClick={() => { pickingRef.current = true; setTimeout(() => { pickingRef.current = false; }, 120000); }}>
              <ImageIcon className="h-4 w-4" />Galeriden Seç
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="barcode-gallery" />
            </label>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <Keyboard className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input placeholder="veya barkodu elle girin" value={manual} inputMode="numeric"
              onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitManual()}
              data-testid="barcode-manual" />
            <Button onClick={submitManual} data-testid="barcode-manual-submit">Ara</Button>
          </div>
          <div className="text-[10px] text-muted-foreground">
            <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setShowLog((s) => !s)} data-testid="scanner-log-toggle"><Bug className="h-3 w-3" />{showLog ? "Tanı günlüğünü gizle" : "Tanı günlüğü (sorun bildirirken bunu paylaşın)"}</button>
            {showLog && (
              <div className="mt-1 rounded-md border bg-muted/40 p-2 font-mono leading-snug max-h-32 overflow-auto whitespace-pre-wrap break-all" data-testid="scanner-log">
                {log.join("\n") || "—"}
                <div className="mt-1"><button type="button" className="inline-flex items-center gap-1 text-primary" onClick={copyLog}><Copy className="h-3 w-3" />Kopyala</button></div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
